import { Box, Chip, Stack, Typography } from '@mui/material';
import type { Block } from './blocks';
import type { BlockSchedules } from '../dataProvider';
import { BLOCK_TYPE_LABEL } from './pageSchemaModel';
import {
  SCHEDULE_COLOR,
  SCHEDULE_LABEL,
  blockScheduleState,
  formatBlockWindow,
  hasWindow,
} from './blockSchedule';

/**
 * The per-page schedule timeline (D-page-schema-editor-8d). A light, read-only list of
 * THIS page's WINDOWED blocks (the ones carrying a visibility window), ordered by their
 * visibleFrom, each with its window and its derived state (scheduled / live / expired)
 * off the injected clock. Blocks with no window are always visible and omitted from the
 * timeline (nothing to schedule). Renders nothing when no block is windowed.
 */
export const ScheduleTimeline = ({
  blocks,
  schedules,
  now,
  testId,
}: {
  blocks: Block[];
  schedules: BlockSchedules;
  now: number;
  testId?: string;
}) => {
  const windowed = blocks
    .map((block) => ({ block, window: schedules[block.id] }))
    .filter((e) => hasWindow(e.window))
    .sort((a, b) =>
      (a.window?.visibleFrom ?? '').localeCompare(b.window?.visibleFrom ?? ''),
    );

  if (windowed.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }} data-testid={testId}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Schedule
      </Typography>
      <Stack spacing={0.5}>
        {windowed.map(({ block, window }) => {
          const state = blockScheduleState(window, now);
          return (
            <Stack
              key={block.id}
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              data-testid={`timeline-entry-${block.id}`}
            >
              <Chip
                size="small"
                variant="outlined"
                color={SCHEDULE_COLOR[state]}
                label={SCHEDULE_LABEL[state]}
              />
              <Typography variant="body2">
                {BLOCK_TYPE_LABEL[block.type]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatBlockWindow(window)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};
